import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UploadSimple, BellRinging } from '@phosphor-icons/react';
import { toast } from 'sonner';
import QuotationPdfUploadDialog from '@/components/QuotationPdfUploadDialog';
import SearchFilterBar, { filterRecords } from '@/components/SearchFilterBar';
import PurchaseOrdersPanel from '@/components/PurchaseOrdersPanel';
import LabRequisitionTable from '@/components/lab/LabRequisitionTable';
import SendRfqDialog from '@/components/lab/SendRfqDialog';
import QuotationsDialog from '@/components/lab/QuotationsDialog';

const PENDING_STATUS_OPTIONS = [
  { value: 'pending_quotation', label: 'Pending Quotation' },
  { value: 'quotations_added', label: 'Quotations Added' },
];

const COMPLETED_STATUS_OPTIONS = [
  { value: 'vendor_approved', label: 'Vendor Approved' },
  { value: 'po_created', label: 'PO Created' },
  { value: 'po_released', label: 'PO Released' },
  { value: 'completed', label: 'Completed' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LabManagerDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [requisitions, setRequisitions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [allQuots, setAllQuots] = useState([]);
  const [selectedReq, setSelectedReq] = useState(null);
  const [rfqTarget, setRfqTarget] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [checkingRfq, setCheckingRfq] = useState(false);

  const [pendingKeyword, setPendingKeyword] = useState('');
  const [pendingStatus, setPendingStatus] = useState('all');
  const [pendingStart, setPendingStart] = useState('');
  const [pendingEnd, setPendingEnd] = useState('');
  const [completedKeyword, setCompletedKeyword] = useState('');
  const [completedStatus, setCompletedStatus] = useState('all');
  const [completedStart, setCompletedStart] = useState('');
  const [completedEnd, setCompletedEnd] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [reqRes, vendorRes, quotRes] = await Promise.all([
        axios.get(`${API}/requisitions`, { headers: getAuthHeader() }),
        axios.get(`${API}/vendors`, { headers: getAuthHeader() }),
        axios.get(`${API}/quotations`, { headers: getAuthHeader() }),
      ]);
      setRequisitions(reqRes.data);
      setVendors(vendorRes.data);
      setAllQuots(quotRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCheckRfqReminders = async () => {
    setCheckingRfq(true);
    try {
      const res = await axios.post(`${API}/check-rfq-reminders`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check RFQ reminders');
    } finally {
      setCheckingRfq(false);
    }
  };

  const pendingRequisitions = requisitions.filter(r => r.status === 'pending_quotation' || r.status === 'quotations_added');
  const completedRequisitions = requisitions.filter(r => !['pending_quotation', 'quotations_added'].includes(r.status));

  const filteredPending = useMemo(
    () => filterRecords(pendingRequisitions, {
      keyword: pendingKeyword, statusValue: pendingStatus,
      startDate: pendingStart, endDate: pendingEnd,
      fields: ['reagent_name', 'vendor_name', 'catalogue_number', 'employee_name'],
    }),
    [pendingRequisitions, pendingKeyword, pendingStatus, pendingStart, pendingEnd]
  );

  const filteredCompleted = useMemo(
    () => filterRecords(completedRequisitions, {
      keyword: completedKeyword, statusValue: completedStatus,
      startDate: completedStart, endDate: completedEnd,
      fields: ['reagent_name', 'vendor_name', 'catalogue_number', 'employee_name'],
    }),
    [completedRequisitions, completedKeyword, completedStatus, completedStart, completedEnd]
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
              Quotation Management
            </h1>
            <p className="mt-2 text-text-secondary">Manage vendor quotations for requisitions</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handleCheckRfqReminders}
              disabled={checkingRfq}
              data-testid="check-rfq-reminders-button"
            >
              <BellRinging size={18} className="mr-2" weight="bold" />
              {checkingRfq ? 'Checking…' : 'RFQ Reminders'}
            </Button>
            <Button
              onClick={() => setPdfDialogOpen(true)}
              className="bg-accent-brand hover:bg-accent-brand-hover text-white"
              data-testid="import-pdf-quotation-button"
            >
              <UploadSimple size={18} className="mr-2" weight="bold" />
              Import Quotation PDF
            </Button>
          </div>
        </div>

        <QuotationPdfUploadDialog
          open={pdfDialogOpen}
          onOpenChange={setPdfDialogOpen}
          onSuccess={loadData}
        />

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="pending" data-testid="pending-tab">Pending ({pendingRequisitions.length})</TabsTrigger>
            <TabsTrigger value="completed" data-testid="completed-tab">Completed ({completedRequisitions.length})</TabsTrigger>
            <TabsTrigger value="pos" data-testid="lab-pos-tab">Purchase Orders</TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="mt-6 space-y-3">
            <SearchFilterBar
              keyword={pendingKeyword} onKeywordChange={setPendingKeyword}
              statusValue={pendingStatus} onStatusChange={setPendingStatus}
              statusOptions={PENDING_STATUS_OPTIONS}
              startDate={pendingStart} endDate={pendingEnd}
              onStartDateChange={setPendingStart} onEndDateChange={setPendingEnd}
              onClear={() => { setPendingKeyword(''); setPendingStatus('all'); setPendingStart(''); setPendingEnd(''); }}
              placeholder="Search reagent, vendor, cat #, employee…"
              testIdPrefix="lab-pending-filter"
            />
            <LabRequisitionTable
              data={filteredPending}
              showActions={true}
              allQuots={allQuots}
              onSendRfq={setRfqTarget}
              onViewQuotations={setSelectedReq}
              onDeleted={loadData}
            />
          </TabsContent>
          <TabsContent value="completed" className="mt-6 space-y-3">
            <SearchFilterBar
              keyword={completedKeyword} onKeywordChange={setCompletedKeyword}
              statusValue={completedStatus} onStatusChange={setCompletedStatus}
              statusOptions={COMPLETED_STATUS_OPTIONS}
              startDate={completedStart} endDate={completedEnd}
              onStartDateChange={setCompletedStart} onEndDateChange={setCompletedEnd}
              onClear={() => { setCompletedKeyword(''); setCompletedStatus('all'); setCompletedStart(''); setCompletedEnd(''); }}
              placeholder="Search reagent, vendor, cat #, employee…"
              testIdPrefix="lab-completed-filter"
            />
            <LabRequisitionTable data={filteredCompleted} showActions={false} allQuots={allQuots} />
          </TabsContent>
          <TabsContent value="pos" className="mt-6">
            <PurchaseOrdersPanel />
          </TabsContent>
        </Tabs>

        {rfqTarget && (
          <SendRfqDialog
            target={rfqTarget}
            vendors={vendors}
            onClose={() => setRfqTarget(null)}
            onSent={() => { setRfqTarget(null); loadData(); }}
          />
        )}

        {selectedReq && (
          <QuotationsDialog
            req={selectedReq}
            onClose={() => setSelectedReq(null)}
            onSaved={loadData}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default LabManagerDashboard;
